import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpBackend, HttpHeaders } from '@angular/common/http';

import { Observable, Subject, catchError, map } from 'rxjs';

import { signInRequestModel, signInResponseModel } from '../models/sign-in';
import { signupRequestModel, signupResponseModel } from '../models/sign-up';
import { validateUserRequestModel, validateUserResponseModel } from '../models/validate-user';
import { resetPasswordRequestModel, resetPasswordResponseModel } from '../models/reset-password';
import { forgotPasswordRequestModel, forgotPasswordResponseModel } from '../models/forgot-password';
import { tokenRequestModel, tokenResponseModel } from '../models/token';
import { signOutRequestModel, signOutResponseModel } from '../models/sign-out';
import { CommonService } from 'src/app/shared/services/common.service';
import { refreshRequestModel, refreshResponseModel } from '../models/refresh';
import { StorageService } from 'src/app/shared/services/storage.service';
import { notificationResponseModel, userGroupResponseModel } from '../models/user-group';
import { apiEndpoints } from 'src/app/shared/enums/api-endpoints';
import { changePasswordRequestModel, changePasswordResponseModel } from '../models/change-password';

@Injectable()
export class AuthenticationService {

  private notification = new Subject<void>();
  tab = this.notification.asObservable();

  private http_client_ro: HttpClient;

  constructor(
    private http_client: HttpClient,
    private http_backend: HttpBackend,
    private common_service: CommonService,
    private storage_service: StorageService,
  ) {
    this.http_client_ro = new HttpClient(this.http_backend);
  }

  get_token() {
    return new HttpHeaders().set('Authorization', 'Bearer ' + this.storage_service.getAccessToken());
  }

  signUp(signupRequest: signupRequestModel): Observable<signupResponseModel> {
    return this.http_client_ro.post<signupResponseModel>(this.common_service.getBaseURL() + apiEndpoints.signUp, signupRequest).pipe(
      map((response: signupResponseModel) =>
        response
      ),
      catchError((error: HttpErrorResponse) => {
        throw new Error('Something went wrong. Please try again later.');
      })
    );
  }

  organizationCreate(signupRequest: signupRequestModel): Observable<signupResponseModel> {
    return this.http_client_ro.post<signupResponseModel>(this.common_service.getBaseURL() + apiEndpoints.organizationCreate, signupRequest).pipe(
      map((response: signupResponseModel) =>
        response
      ),
      catchError((error: HttpErrorResponse) => {
        throw new Error('Something went wrong. Please try again later.');
      })
    );
  }

  validateUser(validateUserRequest: validateUserRequestModel): Observable<validateUserResponseModel> {
    return this.http_client_ro.patch<validateUserResponseModel>(this.common_service.getBaseURL() + apiEndpoints.validateUser, validateUserRequest).pipe(
      map((response: validateUserResponseModel) =>
        response
      )
    );
  }

  signIn(signInRequest: signInRequestModel): Observable<signInResponseModel> {
    return this.http_client_ro.post<signInResponseModel>(this.common_service.getBaseURL() + apiEndpoints.signIn, signInRequest).pipe(
      map((response: signInResponseModel) =>
        response
      )
    );
  }

  signOut(signOutRequest: signOutRequestModel): Observable<signOutResponseModel> {
    return this.http_client.post<signOutResponseModel>(apiEndpoints.signOut, signOutRequest).pipe(
      map((response: signOutResponseModel) =>
        response
      )
    );
  }

  signOutAllDevice(signOutRequest: signOutRequestModel): Observable<signOutResponseModel> {
    return this.http_client.post<signOutResponseModel>(apiEndpoints.signOutAllDevice, signOutRequest).pipe(
      map((response: signOutResponseModel) =>
        response
      )
    );
  }

  getToken(tokenRequest: tokenRequestModel): Observable<tokenResponseModel> {
    return this.http_client_ro.post<tokenResponseModel>(this.common_service.getBaseURL() + apiEndpoints.token, tokenRequest).pipe(
      map((response: tokenResponseModel) =>
        response
      )
    );
  }

  refreshToken(refreshRequest: refreshRequestModel): Observable<refreshResponseModel> {
    return this.http_client_ro.post<refreshResponseModel>(this.common_service.getBaseURL() + apiEndpoints.refresh, refreshRequest).pipe(
      map((response: refreshResponseModel) =>
        response
      )
    );
  }

  forgotPassword(forgotPasswordRequest: forgotPasswordRequestModel): Observable<forgotPasswordResponseModel> {
    return this.http_client_ro.post<forgotPasswordResponseModel>(this.common_service.getBaseURL() + apiEndpoints.forgotPassword, forgotPasswordRequest).pipe(
      map((response: forgotPasswordResponseModel) =>
        response
      )
    );
  }

  resetPassword(resetPasswordRequest: resetPasswordRequestModel): Observable<resetPasswordResponseModel> {
    return this.http_client_ro.patch<resetPasswordResponseModel>(this.common_service.getBaseURL() + apiEndpoints.resetPassword, resetPasswordRequest).pipe(
      map((response: resetPasswordResponseModel) =>
        response
      )
    );
  }

  changePassword(changePasswordRequest: changePasswordRequestModel): Observable<changePasswordResponseModel> {
    return this.http_client.patch<changePasswordResponseModel>(apiEndpoints.changePassword, changePasswordRequest).pipe(
      map((response: changePasswordResponseModel) =>
        response
      )
    );
  }

  fireBaseToken(data: any): Observable<any[]> {
    return this.http_client.post<any[]>(apiEndpoints.fcmToken, data).pipe(
      map((response) =>
        response
      )
    );
  }

  subscribeUser(token: any): Observable<any[]> {
    return this.http_client.post<any[]>(apiEndpoints.subscribeToken, token).pipe(
      map((response: any) =>
        response
      )
    );
  }

  unSubscribeUser(token: any): Observable<any[]> {
    return this.http_client.post<any[]>(apiEndpoints.unSubscribeToken, token).pipe(
      map((response: any) =>
        response
      )
    );
  }

  userGroupddl(page: number, limit: number): Observable<any> {
    return this.http_client.get<userGroupResponseModel>(apiEndpoints.userGroup + `?page=${page}&limit=${limit}`).pipe(
      map((response: userGroupResponseModel) =>
        response
      )
    )
  }

  orgList(token: any): Observable<any> {
    return this.http_client.get<any>(apiEndpoints.orgList, token).pipe(
      map((response: any) => response),
      catchError((error: HttpErrorResponse) => {
        console.error('Error fetching org list:', error);
        throw new Error('Something went wrong. Please try again later.');
      })
    );
  }

  orgSelection(organization_id: any): Observable<any> {
    return this.http_client.post<any>(apiEndpoints.orgaSelection, organization_id).pipe(
      map((response: any) =>
        response
      ),
      catchError((error: HttpErrorResponse) => {
        throw new Error('Something went wrong. Please try again later.');
      })
    )
  }

  loginHistory(page: number, limit: number, staff_id: any): Observable<any> {
    return this.http_client.get<any>(apiEndpoints.user_login_history + `?page=${page}&limit=${limit}&staff_id=${staff_id}`).pipe(
      map((response: any) =>
        response
      ),
      catchError((error: HttpErrorResponse) => {
        throw new Error('Something went wrong. Please try again later.');
      })
    )
  }

  triggerTabUpdate() {
    this.notification.next();
  }

  getNotificationsList(page: number, limit: number): Observable<any> {
    return this.http_client.get<any>(apiEndpoints.notification + `?page=${page}&limit=${limit}`).pipe(
      map((response: notificationResponseModel) =>
        response
      ),
      catchError((error: HttpErrorResponse) => {
        throw new Error('Something went wrong. Please try again later.');
      })
    )
  }

  readNotification(notification_id: string, data:any):Observable<any>{
    return this.http_client.patch<any>(apiEndpoints.notification + notification_id +`/`,data).pipe(
      map((response: any) =>
        response
      ),
      catchError((error: HttpErrorResponse) => {
        throw new Error('Something went wrong. Please try again later.');
      })
    );
  }

  readAllNotification(data:any):Observable<any>{
    return this.http_client.patch<any>(apiEndpoints.notification_read_all + `/`,data).pipe(
      map((response: any) =>
        response
      ),
      catchError((error: HttpErrorResponse) => {
        throw new Error('Something went wrong. Please try again later.');
      })
    );
  }

  unreadAllNotification(data:any):Observable<any>{
    return this.http_client.patch<any>(apiEndpoints.notification_read_all + `/`,data).pipe(
      map((response: any) =>
        response
      ),
      catchError((error: HttpErrorResponse) => {
        throw new Error('Something went wrong. Please try again later.');
      })
    );
  }

  getNotificationCount(): Observable<any> {
    return this.http_client.get<any>(apiEndpoints.notification_count).pipe(
      map((response: any) =>
        response
      ),
      catchError((error: HttpErrorResponse) => {
        throw new Error('Something went wrong. Please try again later.');
      })
    )
  }

  deleteNotification(notification_id: string, data:any):Observable<any>{
    return this.http_client.patch<any>(apiEndpoints.notification + notification_id +`/`,data).pipe(
      map((response: any) =>
        response
      ),
      catchError((error: HttpErrorResponse) => {
        throw new Error('Something went wrong. Please try again later.');
      })
    );
  }

  deleteAllNotification(data:any):Observable<any>{
    return this.http_client.patch<any>(apiEndpoints.notification_read_all +`/`,data).pipe(
      map((response: any) =>
        response
      ),
      catchError((error: HttpErrorResponse) => {
        throw new Error('Something went wrong. Please try again later.');
      })
    );
  }
}
